import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Text } from '@/components/ui/text';
import { useBulkDeleteWorkspacesService } from '@/services/backoffice';
import type { BackOfficeWorkspace } from '@ditto/shared';
import { type FC } from 'react';

interface BulkDeleteWorkspacesModalProps {
  workspaces: BackOfficeWorkspace[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const BulkDeleteWorkspacesModal: FC<BulkDeleteWorkspacesModalProps> = ({
  workspaces,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { bulkDeleteWorkspaces, isPending } = useBulkDeleteWorkspacesService();

  const handleDelete = () => {
    const ids = workspaces.map(w => w.id);
    bulkDeleteWorkspaces(ids, {
      onSuccess: () => {
        onOpenChange(false);
        onSuccess?.();
      },
      onError: error => {
        console.error('Failed to delete workspaces:', error);
      },
    });
  };

  const count = workspaces.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Workspaces</DialogTitle>
          <DialogDescription>
            This action cannot be undone. All workspace data will be permanently
            deleted.
          </DialogDescription>
        </DialogHeader>
        <Text>
          {`Are you sure you want to delete ${count} workspace${count === 1 ? '' : 's'}?`}
        </Text>
        {count <= 5 && (
          <ul className="list-disc pl-5 text-sm text-muted-foreground">
            {workspaces.map(w => (
              <li key={w.id}>{w.name}</li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending}
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
